import { useEffect, useRef, useState } from 'react';
import {
  apercuMessageFormateurs,
  envoyerMessageFormateurs,
  getFormateurs,
} from '../services/formateurService';
import { toast } from '../components/Toasts';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Parametrage.css';
import '../assets/styles/Messagerie.css';

/**
 * Messagerie des formateurs : un message à plusieurs formateurs (tous cochés
 * d'avance, on en décoche), chacun recevant SA version.
 *
 * Deux balises dans le sujet ou le texte : `@mail` (son adresse de connexion)
 * et `@motdepasse` (son mot de passe). Le message est toujours montré avant
 * d'être envoyé.
 */

const simplifie = (t) =>
  String(t || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const MODELE_ACCES = `Bonjour,

Voici vos accès à votre espace formateur :
- Adresse de connexion : @mail
- Mot de passe : @motdepasse

Pensez à remplacer ce mot de passe par le vôtre à votre première connexion (⚙ Paramètres d’accès).

Cordialement,`;

function MessagerieFormateurs() {
  const [formateurs, setFormateurs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState(null);
  // Tous cochés par défaut : on décoche ceux qui ne sont pas concernés.
  const [exclus, setExclus] = useState(() => new Set());
  const [recherche, setRecherche] = useState('');
  const [sujet, setSujet] = useState('');
  const [texte, setTexte] = useState('');
  const [apercu, setApercu] = useState(null);
  const [indexApercu, setIndexApercu] = useState(0);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [bilan, setBilan] = useState(null);
  const zoneTexte = useRef(null);

  useEffect(() => {
    getFormateurs()
      .then((res) => setFormateurs(res.data || []))
      .catch((err) => setErreurChargement(err.message || 'Formateurs indisponibles.'))
      .finally(() => setChargement(false));
  }, []);

  const mots = simplifie(recherche).split(/\s+/).filter(Boolean);
  const visibles = formateurs.filter((f) =>
    mots.every((m) => simplifie([f.prenom, f.nom, f.email, f.fonction].join(' ')).includes(m))
  );
  const choisis = formateurs.filter((f) => !exclus.has(f.id));

  const basculer = (id) =>
    setExclus((prec) => {
      const suite = new Set(prec);
      if (suite.has(id)) suite.delete(id);
      else suite.add(id);
      return suite;
    });

  const toutSelectionner = () =>
    setExclus((prec) => {
      const suite = new Set(prec);
      visibles.forEach((f) => suite.delete(f.id));
      return suite;
    });
  const toutDeselectionner = () =>
    setExclus((prec) => {
      const suite = new Set(prec);
      visibles.forEach((f) => suite.add(f.id));
      return suite;
    });

  /** Insère une balise à l'endroit du curseur dans le message. */
  function inserer(balise) {
    const zone = zoneTexte.current;
    if (!zone) {
      setTexte((t) => `${t}${balise}`);
      return;
    }
    const debut = zone.selectionStart ?? texte.length;
    const fin = zone.selectionEnd ?? texte.length;
    const suite = `${texte.slice(0, debut)}${balise}${texte.slice(fin)}`;
    setTexte(suite);
    setTimeout(() => {
      zone.focus();
      zone.setSelectionRange(debut + balise.length, debut + balise.length);
    }, 0);
  }

  const corps = () => ({ formateurIds: choisis.map((f) => f.id), sujet, texte });

  async function previsualiser(e) {
    e.preventDefault();
    setErreur(null);
    setBilan(null);
    if (!choisis.length) return setErreur('Choisissez au moins un formateur.');
    if (!sujet.trim()) return setErreur('Indiquez l’objet du message.');
    if (!texte.trim()) return setErreur('Écrivez le message.');
    setEnCours(true);
    try {
      const res = await apercuMessageFormateurs(corps());
      setApercu(res.data || []);
      setIndexApercu(0);
    } catch (err) {
      setErreur(err.message || 'Aperçu impossible.');
    } finally {
      setEnCours(false);
    }
  }

  async function envoyer() {
    setEnCours(true);
    setErreur(null);
    try {
      const res = await envoyerMessageFormateurs(corps());
      setBilan(res.data);
      setApercu(null);
      if (res.data.envoyes.length) toast.succes(res.message);
      else toast.erreur(res.message);
    } catch (err) {
      setErreur(err.message || 'Envoi impossible.');
    } finally {
      setEnCours(false);
    }
  }

  const envoyables = apercu ? apercu.filter((a) => a.envoyable) : [];
  const courant = apercu ? apercu[Math.min(indexApercu, apercu.length - 1)] : null;

  return (
    <div className="devis-page devis-page-scroll">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Messagerie des formateurs</h1>
          <p className="devis-subtitle">
            Écrivez à plusieurs formateurs d’un coup. Avec <strong>@mail</strong> et <strong>@motdepasse</strong>, chacun
            reçoit dans son message sa propre adresse de connexion et son propre mot de passe.
          </p>
        </div>
      </div>

      {chargement && <div className="devis-state">Chargement des formateurs…</div>}
      {erreurChargement && <div className="devis-state devis-state-error">{erreurChargement}</div>}

      {!chargement && !erreurChargement && (
        <div className="msg-grille">
          <section className="devis-card msg-destinataires">
            <div className="msg-titre">
              Destinataires <span className="msg-compte">{choisis.length}/{formateurs.length}</span>
            </div>
            <input
              type="search"
              className="devis-search"
              placeholder="Rechercher un formateur…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <div className="msg-raccourcis">
              <button type="button" className="devis-btn devis-btn-sm" onClick={toutSelectionner}>
                Tout sélectionner
              </button>
              <button type="button" className="devis-btn devis-btn-sm" onClick={toutDeselectionner}>
                Tout désélectionner
              </button>
            </div>
            <div className="msg-liste">
              {visibles.length === 0 && <div className="msg-vide">Aucun formateur ne correspond.</div>}
              {visibles.map((f) => (
                <label className="msg-formateur" key={f.id}>
                  <input type="checkbox" checked={!exclus.has(f.id)} onChange={() => basculer(f.id)} />
                  <span className="msg-formateur-texte">
                    <span className="msg-formateur-nom">
                      {[f.prenom, f.nom].filter(Boolean).join(' ')}
                      {!f.actif && <span className="msg-badge">retiré</span>}
                    </span>
                    <span className="msg-formateur-mail">{f.email || 'pas d’adresse e-mail'}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <form className="devis-card msg-redaction" onSubmit={previsualiser}>
            <div className="msg-titre">Message</div>
            <label className="devis-form-field">
              <span>Objet</span>
              <input type="text" value={sujet} onChange={(e) => setSujet(e.target.value)} maxLength={200} />
            </label>
            <label className="devis-form-field">
              <span>Texte</span>
              <textarea ref={zoneTexte} rows={12} value={texte} onChange={(e) => setTexte(e.target.value)} />
            </label>
            <div className="msg-balises">
              <span>Insérer :</span>
              <button type="button" className="devis-btn devis-btn-sm" onClick={() => inserer('@mail')}>
                @mail
              </button>
              <button type="button" className="devis-btn devis-btn-sm" onClick={() => inserer('@motdepasse')}>
                @motdepasse
              </button>
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => {
                  setTexte(MODELE_ACCES);
                  if (!sujet.trim()) setSujet('Vos accès à l’espace formateur');
                }}
              >
                Modèle « mes accès »
              </button>
            </div>
            <p className="msg-note">
              Le mot de passe n’est repris que s’il est encore celui donné à la création du compte. Si le formateur l’a
              changé, le message le lui dit sans l’inventer. Dans le journal des e-mails, le mot de passe est masqué.
            </p>

            {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

            <div className="devis-modal-actions">
              <button type="submit" className="devis-btn devis-btn-primary" disabled={enCours || !choisis.length}>
                {enCours && !apercu ? 'Préparation…' : `Aperçu avant envoi (${choisis.length})`}
              </button>
            </div>

            {bilan && (
              <div className="msg-bilan">
                <div className="msg-bilan-titre">
                  {bilan.envoyes.length} message{bilan.envoyes.length > 1 ? 's' : ''} envoyé
                  {bilan.envoyes.length > 1 ? 's' : ''}
                </div>
                {bilan.ignores.map((i) => (
                  <div key={`i${i.formateurId}`} className="msg-bilan-ligne msg-bilan-ignore">
                    {i.nom} : non envoyé — {i.raison}
                  </div>
                ))}
                {bilan.echecs.map((i) => (
                  <div key={`e${i.formateurId}`} className="msg-bilan-ligne msg-bilan-echec">
                    {i.nom} : échec — {i.raison}
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {apercu && courant && (
        <div className="devis-modal-overlay" onClick={enCours ? undefined : () => setApercu(null)}>
          <div className="devis-modal" onClick={(e) => e.stopPropagation()}>
            <div className="devis-modal-header">
              <div>
                <div className="devis-modal-numero">Aperçu avant envoi</div>
                <div className="devis-modal-sub">
                  {envoyables.length} message{envoyables.length > 1 ? 's' : ''} sur {apercu.length} — chacun avec ses
                  propres valeurs
                </div>
              </div>
              <button type="button" className="devis-modal-close" onClick={() => setApercu(null)} aria-label="Fermer">
                ×
              </button>
            </div>
            <div className="devis-modal-body">
              <label className="devis-form-field">
                <span>Voir ce que reçoit</span>
                <select value={indexApercu} onChange={(e) => setIndexApercu(Number(e.target.value))}>
                  {apercu.map((a, i) => (
                    <option key={a.formateurId} value={i}>
                      {a.nom}
                      {a.envoyable ? '' : ' (non envoyé)'}
                    </option>
                  ))}
                </select>
              </label>
              {!courant.envoyable && <div className="devis-alert devis-alert-erreur">{courant.raison}</div>}
              {courant.motDePasseChange && (
                <div className="devis-alert">
                  Ce formateur a changé son mot de passe : le message lui dit de garder le sien.
                </div>
              )}
              <div className="msg-apercu">
                <div className="msg-apercu-ligne">
                  <span>À</span> {courant.email || '—'}
                </div>
                <div className="msg-apercu-ligne">
                  <span>Objet</span> {courant.sujet}
                </div>
                <pre className="msg-apercu-texte">{courant.texte}</pre>
              </div>
              {apercu.some((a) => !a.envoyable) && (
                <div className="devis-alert">
                  {apercu.filter((a) => !a.envoyable).length} formateur(s) ne recevront rien : {' '}
                  {apercu
                    .filter((a) => !a.envoyable)
                    .map((a) => a.nom)
                    .join(', ')}
                  .
                </div>
              )}
            </div>
            <div className="devis-modal-actions">
              <button type="button" className="devis-btn" onClick={() => setApercu(null)} disabled={enCours}>
                Modifier le message
              </button>
              <button
                type="button"
                className="devis-btn devis-btn-primary"
                onClick={envoyer}
                disabled={enCours || !envoyables.length}
              >
                {enCours ? 'Envoi en cours…' : `Envoyer (${envoyables.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagerieFormateurs;
